import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isDetailsPage, NeuroMessage } from "@ignitesales/neuro-application";
import ContactFormModal from "./ContactFormModal";
import ShareActions from "./ShareActions";
import { useModal } from './ModalProvider';
import { ActionService } from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import { useDispatch, triggerIgniteAction, IgniteCommonActions } from '@ignitesales/neuro-application';
import classnames from "classnames";

const ContactShareButtons = ({
    hideContactButton = false,
    hideShareButton,
    contactButtonRender,
    shareButtonRender,
    useFullWidthButtons,
    isLeftAligned,
    showContactForm: controlledShowContactForm,
    showShareActions: controlledShowShareActions,
    onControlledClose,
    onContactFormButtonClick
}) => {
    const dispatch = useDispatch();
    const { openModal } = useModal();
    const [showContactForm, setShowContactForm] = useState(false);
    const [showShareActions, setShowShareActions] = useState(false);
    const [completeActions, setCompleteActions] = useState([]);
    const [actionRunning, setActionRunning] = useState(false);
    const [actionCompleted, setActionCompleted] = useState(false);

    const componentClass = 'contactShareButtons';
    const classes = classnames(componentClass, {
        [`${componentClass}--fullButtons`]: useFullWidthButtons,
        [`${componentClass}--noLeftMargin`]: isLeftAligned
    });

    // Derive button flags from the configured actions
    const showButtonSpinner = completeActions.some(a => a.showButtonSpinner);
    const disableOnComplete = completeActions.some(a => a.disableOnComplete);

    // Check if there's an action configured for the "complete" button position
    useEffect(() => {
        const fetchCompleteAction = async () => {
            try {
                const actions = await ActionService.getActionByPosition('SummaryPage', 'complete');
                if (Array.isArray(actions) && actions.length > 0) {
                    setCompleteActions(actions);

                    // If any action with disableOnComplete has already fired (fireOnce), start disabled
                    const alreadyFired = actions.some(a => {
                        if (!a.disableOnComplete) return false;
                        const trigger = ActionService.extractButtonTriggerByPosition(a, 'complete');
                        return trigger && ActionService.checkIfActionFiredOnce(a, trigger);
                    });
                    if (alreadyFired) setActionCompleted(true);
                }
            } catch (e) {
                // Fail silently
            }
        };
        fetchCompleteAction();
    }, []);

    // Close on the global closeModal event dispatched by in-layout widget actions,
    // mirroring ActionButton so the SummaryPage "complete" pop/slide also responds.
    useEffect(() => {
        const handleCloseModal = () => {
            setIsActionModalOpen(false);
            setIsActionPanelOpen(false);
        };
        window.addEventListener('ignite:closeModal', handleCloseModal);
        return () => window.removeEventListener('ignite:closeModal', handleCloseModal);
    }, []);

    const onCloseContactModal = () => {
        setShowContactForm(false);
        onControlledClose && onControlledClose('contact');
    };

    const onCloseShareModal = () => {
        setShowShareActions(false);
        onControlledClose && onControlledClose('share');
    };

    const handleCompleteButtonClick = async () => {
        let dontShowDefaultForm = false;
        dispatch(triggerIgniteAction({ actionName: IgniteCommonActions.CONTACT_FORM_CLICKED }));

        // If a custom callback is provided, use it
        if (onContactFormButtonClick) {
            onContactFormButtonClick();
            dontShowDefaultForm = true;
        }

        // If actions are configured for "complete" position, execute all of them
        if (completeActions.length > 0) {
            let anyTriggered = false;
            let openedPop = false;
            let openedSlide = false;
            for (const action of completeActions) {
                const { actionType } = action;
                const trigger = await ActionService.extractButtonTriggerByPosition(action, 'complete');
                if (!trigger) continue;

                anyTriggered = true;
                if (actionType === 'pop' || actionType === 'slide') {
                    // Surface only the first pop and first slide (matches the prior find()-based
                    // render); ModalProvider owns/closes them. Avoids stacking duplicate overlays.
                    const alreadyOpened = actionType === 'pop' ? openedPop : openedSlide;
                    if (!alreadyOpened) {
                        // No `trigger` in the context, deliberately: pre-2576 this rendered
                        // <ActionModal context={{}} /> and never gated on fireOnce. Passing the
                        // trigger would make ModalProvider.markFired() record a close as a
                        // completion, so a fireOnce complete-action would be suppressed on the
                        // next click — a behavior change this refactor must not introduce.
                        openModal(action, {});
                        if (actionType === 'pop') openedPop = true; else openedSlide = true;
                    }
                } else if (actionType === 'run') {
                    try {
                        setActionRunning(true);
                        await ActionService.triggerAction(action, trigger?._id, await ActionService.extractActionParams(action, {}));
                        ActionService.markActionAsFired(action, trigger);
                        if (action.disableOnComplete) setActionCompleted(true);
                    } finally {
                        setActionRunning(false);
                    }
                }
            }
            if (anyTriggered) return;
        }

        // Default behavior: open the contact form modal
        if(!dontShowDefaultForm){
            setShowContactForm(true);
        }
    };

    const shouldShowContactButton = !hideContactButton && !isDetailsPage();
    const isButtonDisabled = (showButtonSpinner && actionRunning) || (disableOnComplete && actionCompleted);

    const renderCompleteButtonContent = () => {
        if (showButtonSpinner && actionRunning) {
            return <><i className="fa fa-spinner fa-spin mr-2" /><NeuroMessage id='summary.completeBtn' /></>;
        }
        if (contactButtonRender) return contactButtonRender;
        return <><i className="ion-md-checkmark-circle-outline mr-2" /><NeuroMessage id='summary.completeBtn' /></>;
    };

    return <div className={classes}>
        {!hideShareButton && <button className={`${componentClass}__button share-btn btn`} onClick={() => setShowShareActions(true)}>
            {!!shareButtonRender ? shareButtonRender : <><i className="ion-md-share mr-2" /><NeuroMessage id="summary.shareBtn" /></>}
        </button>}
        {shouldShowContactButton && <button className={`${componentClass}__button complete-btn btn`} onClick={handleCompleteButtonClick} disabled={isButtonDisabled}>
            {renderCompleteButtonContent()}
        </button>}

        {/* Default contact form modal (when no action override or conditions not met) */}
        {shouldShowContactButton && <ContactFormModal
            isOpen={controlledShowContactForm || showContactForm}
            onClose={onCloseContactModal}
            onComplete={onCloseContactModal}
        />}

        {!hideShareButton && <ShareActions
            isActive={controlledShowShareActions || showShareActions}
            onClose={onCloseShareModal}
            shouldPrintOpenDetailsPage={!isDetailsPage()}
        />}
    </div>;
};

ContactShareButtons.propTypes = {
    hideContactButton: PropTypes.bool,
    hideShareButton: PropTypes.bool,
    isLeftAligned: PropTypes.bool,
    useFullWidthButtons: PropTypes.bool,
    contactButtonRender: PropTypes.node,
    shareButtonRender: PropTypes.node,
    showContactForm: PropTypes.bool,
    showShareActions: PropTypes.bool,
    onControlledClose: PropTypes.func,
    onContactFormButtonClick: PropTypes.func
};

export default ContactShareButtons;
